import type { IAdProvider, SizeMappingEntry } from '../types/ad-manager.interfaces';
import { BannerType } from '../types/google-tag.types';
import type { IDefinedSlot, TAdSlots } from '../types/google-tag.types';

export const GAM_NETWORK_ID = '/21707809931';
export const RESULTS_PER_PAGE = 20;

const ADS_PER_PAGE = 3;

export interface BannerDescriptor {
	containerId: string;
	bannerType: BannerType;
	cssClass: string;
}

export interface BannerClusterDescriptor {
	bannerIds: string[];
	bannerType: BannerType;
	cssClass: string;
}

export type SearchResultItem<T> = T | BannerDescriptor | BannerClusterDescriptor;

export const isBannerDescriptor = (item: unknown): item is BannerDescriptor =>
	typeof item === 'object' && item !== null && 'containerId' in item && 'bannerType' in item;

export const isBannerClusterDescriptor = (item: unknown): item is BannerClusterDescriptor =>
	typeof item === 'object' && item !== null && 'bannerIds' in item && 'bannerType' in item;

export const generateContainerId = (prefix: string, suffix: string | number): string =>
	`div-gpt-ad-${prefix}-${suffix}`;

const firstPageBanners = (): {
	topCluster: BannerClusterDescriptor;
	advanced: BannerDescriptor;
	mobileAdvanced: BannerDescriptor;
} => ({
	topCluster: {
		bannerIds: [
			generateContainerId('22235797574', '0'),
			generateContainerId('22235797574', '1'),
		],
		bannerType: BannerType.TOP,
		cssClass: 'BannerPlaceHolderTopVisibility',
	},
	advanced: {
		containerId: generateContainerId('22205299652', '0'),
		bannerType: BannerType.ADVANCED,
		cssClass: 'BannerPlaceHolderAdvancedVisibility',
	},
	mobileAdvanced: {
		containerId: generateContainerId('22356363616', '0'),
		bannerType: BannerType.MOBILE_ADVANCED,
		cssClass: 'BannerPlaceHolderMobileAdvancedVisibility',
	},
});

const nthPageBanners = (pageNumber: number): {
	topCluster: BannerClusterDescriptor;
	advanced: BannerDescriptor;
	mobileAdvanced: BannerDescriptor;
} => ({
	topCluster: {
		bannerIds: [
			generateContainerId('22235797574', String(pageNumber)),
			generateContainerId('22235797574', `0${pageNumber}0`),
		],
		bannerType: BannerType.TOP,
		cssClass: 'BannerPlaceHolderTopVisibility',
	},
	advanced: {
		containerId: generateContainerId('22205299652', String(pageNumber)),
		bannerType: BannerType.ADVANCED,
		cssClass: 'BannerPlaceHolderAdvancedVisibility',
	},
	mobileAdvanced: {
		containerId: generateContainerId('22356363616', String(pageNumber)),
		bannerType: BannerType.MOBILE_ADVANCED,
		cssClass: 'BannerPlaceHolderMobileAdvancedVisibility',
	},
});

export const bannerInsertionPositions = (pageNumber: number): {
	clusterPosition: number;
	bannerPosition: number;
} => {
	if (pageNumber === 1) {
		return { clusterPosition: 6, bannerPosition: 10 };
	}
	const totalAdsBeforeThisPage = ADS_PER_PAGE * (pageNumber - 1);
	const baseOffset = (pageNumber - 1) * RESULTS_PER_PAGE;
	return {
		clusterPosition: baseOffset + 6 + totalAdsBeforeThisPage,
		bannerPosition: baseOffset + 10 + totalAdsBeforeThisPage,
	};
};

export const interleaveResultsWithBanners = <T>(
	results: T[],
	totalLoadedPages: number,
): { merged: SearchResultItem<T>[]; newBannerPages: number[] } => {
	const merged: SearchResultItem<T>[] = [...results];
	const newBannerPages: number[] = [];

	for (let page = 1; page <= totalLoadedPages; page++) {
		const banners = page === 1 ? firstPageBanners() : nthPageBanners(page);
		const { clusterPosition, bannerPosition } = bannerInsertionPositions(page);

		if (merged.length <= clusterPosition) continue;

		if (merged.length > bannerPosition) {
			// Match SearchPage insertion order
			merged.splice(bannerPosition, 0, banners.advanced);
			merged.splice(bannerPosition, 0, banners.mobileAdvanced);
		}

		merged.splice(clusterPosition, 0, banners.topCluster);
		newBannerPages.push(page);
	}

	return { merged, newBannerPages };
};

export const buildAdUnitPath = (bannerType: BannerType, containerId: string, isMobile: boolean): string => {
	let code: string;
	switch (bannerType) {
	case BannerType.TOP:
		code = 'all__top_visibility/search__top_visibility';
		break;
	case BannerType.ADVANCED:
		code = 'all_pages__advanced_visibility/search__advanced_visibility';
		break;
	case BannerType.MOBILE_ADVANCED:
		code = 'all_pages__mobile_advanced_visibility/search__mobile_advanced_visibility';
		break;
	default:
		// Fallback: desktop advanced for unknown types
		code = isMobile
			? 'all_pages__mobile_advanced_visibility/search__mobile_advanced_visibility'
			: 'all_pages__advanced_visibility/search__advanced_visibility';
	}
	return `${GAM_NETWORK_ID}/${code}/${containerId}`;
};

export const advancedSizeMappingEntries = (): SizeMappingEntry[] => [
	{ viewportSize: [1096, 0], slotSize: [[728, 90]] },
	{ viewportSize: [752, 0], slotSize: [] },
	{ viewportSize: [743, 0], slotSize: [[728, 90]] },
];

export const mobileAdvancedSizeMappingEntries = (): SizeMappingEntry[] => [
	{ viewportSize: [1096, 0], slotSize: [] },
	{ viewportSize: [752, 0], slotSize: [[320, 50]] },
	{ viewportSize: [743, 0], slotSize: [] },
	{ viewportSize: [352, 0], slotSize: [[320, 50]] },
];

export const leftTopSizeMappingEntries = (): SizeMappingEntry[] => [
	{ viewportSize: [332, 0], slotSize: [[300, 250]] },
];

export const rightTopSizeMappingEntries = (): SizeMappingEntry[] => [
	{ viewportSize: [984, 0], slotSize: [[300, 250]] },
	{ viewportSize: [752, 0], slotSize: [] },
	{ viewportSize: [648, 0], slotSize: [[300, 250]] },
];

export const getSizeMappingForBanner = (
	bannerType: BannerType,
	clusterIndex: number,
	isMobile: boolean,
): { defaultSize: [number, number]; entries: SizeMappingEntry[] } => {
	switch (bannerType) {
	case BannerType.ADVANCED:
		return { defaultSize: [728, 90], entries: isMobile ? mobileAdvancedSizeMappingEntries() : advancedSizeMappingEntries() };
	case BannerType.MOBILE_ADVANCED:
		return { defaultSize: [320, 50], entries: mobileAdvancedSizeMappingEntries() };
	case BannerType.TOP:
		return {
			defaultSize: [300, 250],
			entries: clusterIndex === 0 ? leftTopSizeMappingEntries() : rightTopSizeMappingEntries(),
		};
	default:
		return { defaultSize: [728, 90], entries: advancedSizeMappingEntries() };
	}
};

export const registerBannerSlot = (
	provider: IAdProvider,
	bannerType: BannerType,
	containerId: string,
	clusterIndex: number,
): IDefinedSlot | undefined => {
	const adWindow = window as unknown as { adSlots?: TAdSlots };
	const adSlots: TAdSlots = adWindow.adSlots ?? {};
	const slots: IDefinedSlot[] = adSlots[bannerType] ?? [];
	const existingSlot = slots.find((slot) => slot.getSlotElementId() === containerId);
	if (existingSlot) return existingSlot;

	const { defaultSize, entries } = getSizeMappingForBanner(bannerType, clusterIndex, false);
	const definedSlot = provider.defineSlot(
		buildAdUnitPath(bannerType, containerId, false),
		defaultSize,
		containerId,
	);
	if (!definedSlot) return undefined;

	const sizeMapping = provider.buildSizeMapping(entries);
	provider.addSizeMappingToSlot(definedSlot, sizeMapping!);
	const registeredSlot = provider.addServiceToSlot(definedSlot);
	if (!registeredSlot) return undefined;

	adSlots[bannerType] = [...slots, registeredSlot];
	adWindow.adSlots = adSlots;
	return registeredSlot;
};
